import React, { useEffect, useState, memo, useMemo, useCallback } from "react";
import {
  collection,
  doc,
  getDocs,
  getFirestore,
  getDoc,
  query,
  setDoc,
  where,
  addDoc,
  or,
} from "firebase/firestore";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Bed,
  Users,
  User,
  Clock,
  AlertTriangle,
  Info,
  FileText,
  ClipboardList,
  BarChart3,
  ShieldOff,
} from "lucide-react";
import { Helmet } from "react-helmet";
import moment from "moment";
import "moment-duration-format";
import { useNavigate } from "react-router-dom";
import { Spinner } from "../components";
import { useAuth } from "../hooks/AuthenticationProvider";
import SettingsModal from "../components/Modals/SettingsModal";
import {
  SubscriptionProvider,
  SubscriptionBanner,
  SubscriptionGate,
  useSubscription, // Add this
} from "../utils/SubscriptionManager.jsx";

const Modal = ({ isOpen, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
      <div className="bg-slate-950/95 border border-slate-800 rounded-xl shadow-xl w-96">
        <div className="px-6 py-4 border-b border-slate-800">
          <h2 className="text-2xl font-semibold text-white">{title}</h2>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const calculateTimeSinceLastRelapse = (relapseDate) => {
  if (!relapseDate || !moment(relapseDate).isValid()) return null;

  const startDate = moment(relapseDate).startOf("day");
  const now = moment().startOf("day");

  // Use moment.duration for accurate calculations
  const duration = moment.duration(now.diff(startDate));

  return {
    years: duration.years(),
    months: duration.months(),
    // Use duration.days() which correctly accounts for varying month lengths
    days: duration.days(),
  };
};

const getMostRecentRelapseDate = (house, residents, alumni) => {
  // First check among current residents who relapsed
  const recentFromResidents = residents
    .filter(
      (resident) =>
        resident.sobrietyStatus === "Not Sober" &&
        resident.house === house &&
        resident.dischargeDate
    )
    .reduce((mostRecent, resident) => {
      const dischargeDate = moment(resident.dischargeDate, "YYYY-MM-DD");

      if (!dischargeDate.isValid()) {
        return mostRecent;
      }

      return !mostRecent || dischargeDate.isAfter(mostRecent)
        ? dischargeDate.toDate()
        : mostRecent;
    }, null);

  // Then check among alumni who relapsed
  const recentFromAlumni = alumni
    .filter(
      (alumnus) =>
        alumnus.sobrietyStatus === "Not Sober" &&
        alumnus.house === house &&
        alumnus.dischargeDate
    )
    .reduce((mostRecent, alumnus) => {
      const dischargeDate = moment(alumnus.dischargeDate, "YYYY-MM-DD");

      if (!dischargeDate.isValid()) {
        return mostRecent;
      }

      return !mostRecent || dischargeDate.isAfter(mostRecent)
        ? dischargeDate.toDate()
        : mostRecent;
    }, null);

  // Compare the most recent date from both collections
  if (!recentFromResidents && !recentFromAlumni) return null;
  if (!recentFromResidents) return recentFromAlumni;
  if (!recentFromAlumni) return recentFromResidents;

  // Return the more recent date between the two
  return moment(recentFromResidents).isAfter(moment(recentFromAlumni))
    ? recentFromResidents
    : recentFromAlumni;
};

const useRelapseTracking = (
  residents,
  housesData,
  organizationId,
  db,
  externalAlumni = null
) => {
  const [timeSinceLastRelapse, setTimeSinceLastRelapse] = useState({});
  const [alumni, setAlumni] = useState([]);

  // Alumni data effect - either use external data or fetch it
  useEffect(() => {
    // If external alumni data is provided, use it
    if (externalAlumni) {
      setAlumni(externalAlumni);
      return;
    }

    // Otherwise fetch alumni data
    const fetchAlumni = async () => {
      if (!organizationId) return;

      try {
        const alumniRef = collection(
          db,
          `organizations/${organizationId}/alumni`
        );
        const alumniSnapshot = await getDocs(alumniRef);
        const alumniData = alumniSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAlumni(alumniData);
      } catch (error) {
        console.error("Error fetching alumni data:", error);
      }
    };

    fetchAlumni();
  }, [organizationId, db, externalAlumni]); // Add externalAlumni as dependency

  // Calculate relapse times effect
  useEffect(() => {
    if (!housesData || !residents || Object.keys(housesData).length === 0)
      return;

    const relapseTimes = Object.keys(housesData).reduce((acc, house) => {
      const mostRecentRelapseDate = getMostRecentRelapseDate(
        house,
        residents,
        alumni
      );

      // Handle the first relapse for each house
      const allResidents = [...residents, ...alumni];
      const firstRelapseTime = allResidents
        .filter(
          (person) =>
            person.house === house && person.sobrietyStatus === "Not Sober"
        )
        .map((person) => calculateFirstRelapseTime(person))
        .filter(Boolean) // Remove null values
        .sort((a, b) => {
          // Sort by earliest relapse (by years, months, days)
          if (a.years !== b.years) return a.years - b.years;
          if (a.months !== b.months) return a.months - b.months;
          return a.days - b.days;
        })[0]; // Get the earliest relapse time

      acc[house] = mostRecentRelapseDate
        ? calculateTimeSinceLastRelapse(mostRecentRelapseDate)
        : firstRelapseTime || "No relapse yet";
      return acc;
    }, {});

    setTimeSinceLastRelapse(relapseTimes);
  }, [residents, housesData, alumni]);

  const formatRelapseTime = useCallback((time) => {
    if (!time || time === "No relapse yet") return "No relapse yet";
    const parts = [];
    if (time.years > 0) parts.push(`${time.years}y`);
    if (time.months > 0 || time.years > 0) parts.push(`${time.months}m`);
    parts.push(`${time.days}d`);
    return parts.join(" ");
  }, []);

  return {
    timeSinceLastRelapse,
    formatRelapseTime,
  };
};

const calculateFirstRelapseTime = (resident) => {
  if (!resident.intakeDate || !resident.dischargeDate) return null;
  const intakeDate = moment(resident.intakeDate, "YYYY-MM-DD");
  const dischargeDate = moment(resident.dischargeDate, "YYYY-MM-DD");

  if (!intakeDate.isValid() || !dischargeDate.isValid()) {
    console.warn("Invalid date format for resident:", resident.fullName);
    return null;
  }

  return {
    years: dischargeDate.diff(intakeDate, "years"),
    months: dischargeDate.diff(intakeDate, "months") % 12,
    days: dischargeDate.diff(intakeDate, "days") % 30,
  };
};

const initializeHighs = (housesData) => {
  // Create base object with total metrics
  let initialHighs = {
    totalSobriety: -Infinity,
    averageLengthOfTime: -Infinity,
  };

  // Add house-specific metrics
  Object.keys(housesData).forEach((houseName) => {
    // Sobriety and relapse records per house
    initialHighs[`${houseName}Sobriety`] = -Infinity;
    initialHighs[`${houseName}Relapse`] = -Infinity;
    initialHighs[`${houseName}AverageLengthOfStay`] = -Infinity;
  });

  return initialHighs;
};

const isGreater = (s1, s2) => {
  if (!s1 || typeof s1 !== "object") return false;
  if (s2 === -Infinity) return true;
  if (!s2 || s2 === -Infinity) return true;

  const duration1 = moment.duration({
    years: s1.years || 0,
    months: s1.months || 0,
    days: s1.days || 0,
  });

  const duration2 = moment.duration({
    years: s2.years || 0,
    months: s2.months || 0,
    days: s2.days || 0,
  });

  return duration1.asMilliseconds() > duration2.asMilliseconds();
};

const calculateSobrietyPercentage = (sobrietyTime, maxSobriety) => {
  if (!sobrietyTime || !maxSobriety || maxSobriety.years === 0) return 100;

  // Convert everything to numbers to ensure proper calculation
  const totalDays =
    sobrietyTime.years * 365 +
    Number(sobrietyTime.months) * 30 +
    Number(sobrietyTime.days);

  const maxDays =
    Number(maxSobriety.years) * 365 +
    Number(maxSobriety.months) * 30 +
    Number(maxSobriety.days);

  const percentage = (totalDays / maxDays) * 100;

  // If breaking record (>100%) or no valid comparison, return 100
  return percentage > 100 ? 100 : percentage;
};

const ReoccurrenceGrid = ({ timeSinceLastRelapse, formatRelapseTime }) => {
  const itemCount = Object.keys(timeSinceLastRelapse).length;

  const gridClasses = `grid gap-4 w-full mx-auto ${
    itemCount <= 2
      ? "grid-cols-1 sm:grid-cols-2 max-w-3xl h-full place-content-center"
      : itemCount <= 3
      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl"
      : itemCount <= 6
      ? "grid-cols-2 lg:grid-cols-3 max-w-5xl"
      : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 max-w-6xl"
  }`;

  const cardClasses = `group relative bg-slate-800/30 rounded-xl border border-amber-200/10 hover:border-amber-200/20 hover:shadow-lg hover:shadow-amber-200/10 transition-all duration-300 ${
    itemCount <= 2 ? "h-40" : "h-28"
  }`;

  return (
    <div className={gridClasses}>
      {Object.entries(timeSinceLastRelapse).map(([houseName, relapseTime]) => (
        <div key={houseName} className={cardClasses}>
          <div className="absolute inset-0 bg-gradient-to-b from-amber-600/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
          <div className="h-full p-4 flex flex-col items-center justify-center">
            <h3 className="text-slate-200 font-medium mb-2 text-sm">
              {houseName}
            </h3>
            {relapseTime === "No relapse yet" ? (
              <p className="text-sm text-green-400 font-medium">
                No reoccurrence recorded
              </p>
            ) : (
              <p className="text-xl font-bold text-amber-200">
                {formatRelapseTime(relapseTime)}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const SobrietyCard = memo(
  ({
    houseName,
    houseData,
    sobriety,
    highs,
    hoveredCard,
    onHover,
    onLeave,
  }) => {
    const isHigh = useMemo(() => {
      const hasSobrietyTime =
        sobriety &&
        (sobriety.years > 0 || sobriety.months > 0 || sobriety.days > 0);
      if (!hasSobrietyTime) return false;
      if (houseName === "total") {
        if (!highs.totalSobriety || highs.totalSobriety === -Infinity) {
          return true;
        }
        return isGreater(sobriety, highs.totalSobriety);
      }
      const houseRecord = highs[`${houseName}Sobriety`];
      if (!houseRecord || houseRecord === -Infinity) {
        return true;
      }
      return isGreater(sobriety, houseRecord);
    }, [houseName, sobriety, highs]);

    const percentage =
      houseName === "total"
        ? sobriety &&
          (sobriety.years > 0 || sobriety.months > 0 || sobriety.days > 0)
          ? !highs.totalSobriety || highs.totalSobriety === -Infinity
            ? 100
            : calculateSobrietyPercentage(sobriety, highs.totalSobriety)
          : 0
        : sobriety &&
          (sobriety.years > 0 || sobriety.months > 0 || sobriety.days > 0)
        ? highs[`${houseName}Sobriety`] &&
          highs[`${houseName}Sobriety`] !== -Infinity
          ? calculateSobrietyPercentage(sobriety, highs[`${houseName}Sobriety`])
          : 100
        : 0;

    return (
      <div
        className="flex-1 flex flex-col items-center justify-between h-full w-full" // Added items-center and justify-between
        onMouseEnter={() => onHover(houseName)}
        onMouseLeave={() => onLeave()}
      >
        <div
          className={`absolute inset-0 bg-gradient-to-b ${
            isHigh ? "from-slate-800/30" : "from-slate-800/20"
          } to-transparent rounded-xl transform -skew-y-2`}
        />

        <SobrietyCardContent
          houseName={houseName}
          houseData={houseData}
          sobriety={sobriety}
          isHigh={isHigh}
        />

        <div className="flex flex-col items-center w-full">
          {" "}
          {/* Added wrapper for progress bar */}
          <SobrietyProgressBar
            percentage={percentage}
            houseName={houseName}
            houseData={houseData}
            isHigh={isHigh}
            showTooltip={hoveredCard === houseName}
          />
        </div>
      </div>
    );
  }
);

const SobrietyCardContent = memo(
  ({ houseName, houseData, sobriety, isHigh }) => (
    <div className="flex-1 flex flex-col items-center justify-start pt-4">
      <h3 className="text-slate-200 font-medium mb-2 text-lg">
        {houseName === "total" ? "Total" : houseName}
      </h3>
      <p
        style={{
          color: isHigh
            ? houseName === "total"
              ? "rgb(96, 165, 250)"
              : houseData.color
            : "white",
        }}
        className={`text-xl font-bold text-center ${
          isHigh ? "animate-pulse" : ""
        }`}
      >
        {sobriety
          ? `${sobriety.years}y ${sobriety.months}m ${sobriety.days}d`
          : "0y 0m 0d"}
      </p>
      <div className="h-5 mt-2 mb-4 w-full flex items-center justify-center">
        {isHigh && (
          <span
            style={{
              color:
                houseName === "total" ? "rgb(96, 165, 250)" : houseData.color,
            }}
            className="text-xs font-semibold animate-pulse text-center inline-block w-full"
          >
            Breaking Record!🏆
          </span>
        )}
      </div>
    </div>
  )
);

const SobrietyProgressBar = memo(
  ({ percentage, houseName, houseData, isHigh, showTooltip }) => (
    <div className="relative w-4 h-28 mb-8 group/progress">
      <div className="absolute inset-0 bg-slate-700/70 rounded-full overflow-hidden shadow-inner">
        <div
          className="absolute bottom-0 w-full bg-gradient-to-t transition-all duration-500 ease-in-out"
          style={{
            height: `${percentage}%`,
            background:
              houseName === "total"
                ? "linear-gradient(to top, rgb(59, 130, 246), rgb(147, 197, 253))"
                : `linear-gradient(to top, ${houseData.color}, ${houseData.color}BB)`,
          }}
        />
      </div>

      <ProgressBarOrb
        houseName={houseName}
        houseData={houseData}
        isHigh={isHigh}
      />

      {showTooltip && (
        <div className="absolute -left-24 top-1/2 transform -translate-y-1/2 transition-opacity duration-300 pointer-events-none h-6">
          <div className="bg-slate-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap border border-slate-700/50">
            {percentage.toFixed(1)}% of record
          </div>
        </div>
      )}
    </div>
  )
);

const ProgressBarOrb = memo(({ houseName, houseData, isHigh }) => (
  <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
    <div
      className={`absolute inset-0 blur-lg transform scale-150 opacity-50 group-hover/card:opacity-75 transition-opacity duration-300 ${
        isHigh ? "animate-pulse" : ""
      }`}
      style={{
        background:
          houseName === "total" ? "rgb(59, 130, 246)" : houseData.color,
      }}
    />
    <div
      className="relative w-8 h-8 rounded-full shadow-lg border border-white/20 transform scale-100 group-hover/card:scale-110 transition-transform duration-500"
      style={{
        background:
          houseName === "total" ? "rgb(59, 130, 246)" : houseData.color,
      }}
    >
      <div className="absolute top-1 left-1 w-2 h-2 bg-white rounded-full opacity-50" />
    </div>
  </div>
));

const Dashboard = () => {
  const {
    user,
    isLoading,
    organizationId,
    role,
    checkFeature,
    checkPermission,
    organizationData,
  } = useAuth();
  const bedPlatformEnabled = checkFeature("bedPlatform");
  const organizationName = organizationData?.organizationName || "";
  const housesData = organizationData?.housesData || {};
  const residents = organizationData?.residents || [];
  const navigate = useNavigate();
  const db = getFirestore();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [totalHelped, setTotalHelped] = useState(0);
  const [totalSavings, setTotalSavings] = useState(0);
  const [sobrietyData, setSobrietyData] = useState({});
  const [recordsFilter, setRecordsFilter] = useState("total");
  const [highs, setHighs] = useState(() => initializeHighs(housesData));
  const [isAddBanModalOpen, setIsAddBanModalOpen] = useState(false);
  const [showDashboardDropdown, setShowDashboardDropdown] = useState(false);
  const [isBannedModalOpen, setIsBannedModalOpen] = useState(false);
  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [recordTypeFilter, setRecordTypeFilter] = useState("sobriety");
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [isUAListModalOpen, setUAListModalOpen] = useState(false);
  const [alumni, setAlumni] = useState([]);
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [uaResidents, setUAResidents] = useState([]);
  const [bannedList, setBannedList] = useState([]);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [banName, setBanName] = useState("");
  const [allTimeHighs, setAllTimeHighs] = useState([]);
  const [showBedUpdateModal, setShowBedUpdateModal] = useState(false);
  const { timeSinceLastRelapse, formatRelapseTime } = useRelapseTracking(
    residents,
    housesData,
    organizationId,
    db,
    alumni
  );
  const [documents, setDocuments] = useState([]);

  const DynamicCard = ({ children, houseData, houseName, compact }) => {
    return (
      <div className="relative">
        <div
          style={{
            "--border-color":
              houseName === "total"
                ? "rgba(59, 130, 246, 0.2)"
                : `${houseData.color}30`,
            "--hover-border-color":
              houseName === "total"
                ? "rgba(59, 130, 246, 0.4)"
                : `${houseData.color}`,
          }}
          className={`relative p-4 backdrop-blur-sm rounded-xl border border-[var(--border-color)] 
                hover:border-[var(--hover-border-color)] transition-all duration-500 flex flex-col items-center 
                group/card shadow-lg hover:shadow-xl hover:shadow-slate-900/20 
                ${compact ? "h-40" : "h-72"}`}
        >
          {children}
        </div>
      </div>
    );
  };

  // Dynamic grid layout component
  const DynamicGrid = ({ items, renderItem }) => {
    const getGridClasses = () => {
      const count = items.length;
      const baseClasses = "grid gap-4 w-full mx-auto p-4";

      if (count === 1) {
        return `${baseClasses} grid-cols-1 max-w-md`;
      }

      if (count <= 3) {
        return `${baseClasses} grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl`;
      }

      if (count <= 6) {
        return `${baseClasses} grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl`;
      }

      return `${baseClasses} grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 max-w-7xl`;
    };

    const getCardHeight = (compact) => {
      return compact ? "h-40" : "h-72";
    };

    return (
      <div className={getGridClasses()}>
        {items.map(([houseName, relapseTime], index) => (
          <div
            key={index}
            className={getCardHeight(relapseTime === "No relapse yet")}
          >
            {renderItem([houseName, relapseTime])}
          </div>
        ))}
      </div>
    );
  };

  // Add this at the top of your Dashboard component
  useEffect(() => {
    if (!organizationId) return;

    const fetchAllData = async () => {
      try {
        // Fetch all needed data in parallel
        const [alumniSnapshot, weeklySnapshotsSnapshot, documentsSnapshot] =
          await Promise.all([
            // Alumni data
            getDocs(collection(db, `organizations/${organizationId}/alumni`)),
            // Weekly snapshots
            getDocs(
              collection(db, `organizations/${organizationId}/weeklySnapshots`)
            ),
            // Documents
            getDocs(
              collection(
                db,
                `organizations/${organizationId}/importantDocuments`
              )
            ),
          ]);

        // Process the results
        const alumniData = alumniSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const snapshots = weeklySnapshotsSnapshot.docs.map((doc) => doc.data());
        const docs = documentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Update state
        setAlumni(alumniData);
        setDocuments(docs);
        calculateAllTimeHighs(snapshots);

        // Calculate total people helped
        setTotalHelped(residents.length + alumniData.length);

        // Calculate savings
        let totalSavingsAmount = 0;

        // Calculate for residents
        residents.forEach((resident) => {
          totalSavingsAmount += calculateSavingsPerResident(
            resident.intakeDate,
            resident.currentResident ? null : resident.dischargeDate
          );
        });

        // Calculate for alumni
        alumniData.forEach((alumnus) => {
          totalSavingsAmount += calculateSavingsPerResident(
            alumnus.intakeDate,
            alumnus.dischargeDate
          );
        });

        setTotalSavings(totalSavingsAmount);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchAllData();
  }, [organizationId, residents]); // Only depends on organizationId and residents

  // LAZY-LOADING FOR MODALS - only fetch when needed
  useEffect(() => {
    if (isBannedModalOpen && organizationId) {
      fetchBannedResidents();
    }
  }, [isBannedModalOpen, organizationId]);

  useEffect(() => {
    if (isUAListModalOpen && selectedHouse && organizationId) {
      fetchUAResidents();
    }
  }, [isUAListModalOpen, selectedHouse, organizationId]);

  const fetchDocuments = async () => {
    if (!organizationId) return;

    try {
      const docsRef = collection(
        db,
        `organizations/${organizationId}/importantDocuments`
      );
      const querySnapshot = await getDocs(docsRef);
      const docs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setDocuments(docs);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  useEffect(() => {
    if (organizationData?.needsBedUpdate) {
      setShowBedUpdateModal(true);
    }
  }, [organizationData]);

  const isGreater = (s1, s2) => {
    // Early return if either value is invalid
    if (!s1 || typeof s1 !== "object") return false;
    if (s2 === -Infinity) return true;
    if (!s2 || s2 === -Infinity) return true; // Changed to true to match initialization logic

    // Convert both times to total days for simpler comparison
    const s1Days = s1.years * 365 + s1.months * 30 + s1.days;
    const s2Days = s2.years * 365 + s2.months * 30 + s2.days;

    return s1Days > s2Days;
  };

  const fetchWeeklySnapshots = async () => {
    const weeklySnapshotsRef = collection(
      db,
      `organizations/${organizationId}/weeklySnapshots`
    );
    try {
      const querySnapshot = await getDocs(weeklySnapshotsRef);
      const snapshots = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return data;
      });
      calculateAllTimeHighs(snapshots);
    } catch (error) {
      console.error("Error fetching weekly snapshots:", error);
    }
  };

  const transformWeeklySnapshot = (snapshot) => {
    if (!snapshot) {
      console.log("No snapshot data available");
      return {};
    }

    const highs = {};

    // Handle sobriety times
    if (snapshot.sobrietyTimes) {
      Object.entries(snapshot.sobrietyTimes).forEach(
        ([houseName, sobrietyData]) => {
          highs[`${houseName}Sobriety`] = {
            years: Number(sobrietyData.years) || 0, // Convert to number
            months: Number(sobrietyData.months) || 0, // Convert to number
            days: Number(sobrietyData.days) || 0, // Convert to number
          };
        }
      );
    }

    // Add total sobriety separately since it's at the root level
    if (snapshot.totalSobriety) {
      highs["totalSobriety"] = {
        years: snapshot.totalSobriety.years,
        months: snapshot.totalSobriety.months,
        days: snapshot.totalSobriety.days,
      };
    }

    Object.entries(snapshot).forEach(([key, value]) => {
      if (
        key.toLowerCase().includes("relapse") &&
        value &&
        typeof value === "object"
      ) {
        highs[key] = {
          // Just use the key directly since it's already in the right format
          years: Number(value.years) || 0,
          months: Number(value.months) || 0,
          days: Number(value.days) || 0,
        };
      }
    });

    // Handle average length of stay data
    if (snapshot.averageLengthOfStay) {
      // Process each house's average stay
      Object.entries(snapshot.averageLengthOfStay).forEach(
        ([houseName, value]) => {
          // Skip the 'total' entry as we'll handle it separately
          if (houseName.toLowerCase() !== "total") {
            highs[`${houseName}AverageLengthOfStay`] = value;
          }
        }
      );

      // Add the total average length of stay
      if (snapshot.averageLengthOfStay.total) {
        highs["averageLengthOfTime"] = snapshot.averageLengthOfStay.total;
      }
    }

    return highs;
  };

  useEffect(() => {
    const filtered = allTimeHighs.filter((high) => {
      // First, check if this is a valid record
      if (!high || !high.label) return false;

      // Continue with your existing type filtering
      if (recordTypeFilter === "sobriety") {
        return high.label.includes("Sobriety");
      }
      if (recordTypeFilter === "relapse") {
        return high.label.includes("Relapse");
      }
      if (recordTypeFilter === "averageStay") {
        return (
          high.label.includes("AverageLengthOfStay") ||
          high.label === "averageLengthOfTime"
        );
      }
      return false;
    });

    setFilteredRecords(filtered);
  }, [recordTypeFilter, allTimeHighs]);

  const calculateAllTimeHighs = (snapshots) => {
    const highsObj = {};

    snapshots.forEach((snapshot, index) => {
      const transformedHighs = transformWeeklySnapshot(snapshot);

      Object.keys(transformedHighs).forEach((key) => {
        const currentValue = highsObj[key];
        const newValue = transformedHighs[key];

        if (!currentValue || (newValue && isGreater(newValue, currentValue))) {
          highsObj[key] = newValue;
        }
      });
    });

    const highEntries = Object.entries(highsObj).map(([label, value]) => ({
      label,
      value,
    }));

    setHighs(highsObj);
    setAllTimeHighs(highEntries);
  };

  const fetchBannedResidents = async () => {
    if (!organizationId) return;

    try {
      // Fetch banned residents from residents collection
      const residentsQuery = query(
        collection(db, `organizations/${organizationId}/residents`),
        or(
          where("returnability", "==", "no"),
          where("returnability", "==", "No")
        )
      );
      const residentsSnapshot = await getDocs(residentsQuery);
      const bannedResidents = residentsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch banned alumni
      const alumniQuery = query(
        collection(db, `organizations/${organizationId}/alumni`),
        or(
          where("returnability", "==", "no"),
          where("returnability", "==", "No")
        )
      );
      const alumniSnapshot = await getDocs(alumniQuery);
      const bannedAlumni = alumniSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Combine both lists
      setBannedList([...bannedResidents, ...bannedAlumni]);
    } catch (error) {
      console.error("Error fetching banned residents:", error);
    }
  };

  // Fetch the banned list **only when the modal opens**
  useEffect(() => {
    if (isBannedModalOpen) {
      fetchBannedResidents();
    }
  }, [isBannedModalOpen]);

  const fetchUAResidents = async () => {
    if (!selectedHouse || !organizationId) return;

    const q = query(
      collection(db, `organizations/${organizationId}/residents`),
      where("currentResident", "==", true),
      where("house", "==", selectedHouse)
    );

    const snapshot = await getDocs(q);
    setUAResidents(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  // Fetch UA residents **only when the modal opens or the house selection changes**
  useEffect(() => {
    if (isUAListModalOpen) {
      fetchUAResidents();
    }
  }, [isUAListModalOpen, selectedHouse]);

  // AUD, other drugs, + opioids  |  all in 2025 dollars
  //  • $354 B  alcohol (CDC 2010 → CPI-adj)
  //  • $290 B  non-opioid illicit drugs (DOJ 2007 → CPI-adj)
  //  • $1.22 T opioids (CDC 2017 → CPI-adj)

  const calculateSavingsPerResident = (intakeDate, dischargeDate = null) => {
    if (!intakeDate) return 0;

    const startDate = moment(intakeDate, "YYYY-MM-DD");
    const endDate = dischargeDate
      ? moment(dischargeDate, "YYYY-MM-DD")
      : moment();
    const totalDays = endDate.diff(startDate, "days");

    const totalAnnualImpact =
      354_000_000_000 + 290_000_000_000 + 1_220_000_000_000;
    const annualImpactPerPerson = totalAnnualImpact / 50_000_000; // 2022 NSDUH SUD population
    const dailyImpact = annualImpactPerPerson / 365;

    return Math.max(0, totalDays * dailyImpact);
  };

  useEffect(() => {
    if (residents.length > 0) {
      const data = {};
      Object.keys(housesData).forEach((house) => {
        const houseSobriety = calculateHouseSobriety(house);
        data[house] = houseSobriety || { years: 0, months: 0, days: 0 };
      });
      setSobrietyData(data);
    }
  }, [residents, housesData]);

  const calculateHouseSobriety = useCallback(
    (house) => {
      if (residents.length === 0) return null;

      // Only current residents count toward house sobriety
      const filteredResidents = residents.filter(
        (resident) => resident.house === house && resident.currentResident
      );

      return filteredResidents.reduce(
        (total, resident) => {
          if (!resident.sobrietyDate) return total;
          const sobrietyTime = calculateLengthOfSobriety(resident.sobrietyDate);
          return addSobrietyTimes(total, sobrietyTime);
        },
        { years: 0, months: 0, days: 0 }
      );
    },
    [residents]
  );

  const calculateLengthOfSobriety = (sobrietyDate) => {
    if (!sobrietyDate) return null;
    const start = moment(sobrietyDate, "YYYY-MM-DD").startOf("day");
    const end = moment().startOf("day"); // This is the key change
    if (!start.isValid()) {
      console.warn(`Invalid sobriety date format: ${sobrietyDate}`);
      return null;
    }
    const duration = moment.duration(end.diff(start));
    return {
      years: duration.years(),
      months: duration.months(),
      days: duration.days(),
    };
  };

  const addSobrietyTimes = (time1, time2) => {
    if (!time1 && !time2) return { years: 0, months: 0, days: 0 };
    if (!time1) return time2;
    if (!time2) return time1;

    const duration1 = moment.duration({
      years: time1.years || 0,
      months: time1.months || 0,
      days: time1.days || 0,
    });

    const duration2 = moment.duration({
      years: time2.years || 0,
      months: time2.months || 0,
      days: time2.days || 0,
    });

    const totalDuration = duration1.add(duration2);

    return {
      years: totalDuration.years(),
      months: totalDuration.months(),
      days: totalDuration.days(),
    };
  };

  const totalSobriety = Object.values(sobrietyData).reduce(
    (total, houseSobriety) => addSobrietyTimes(total, houseSobriety),
    { years: 0, months: 0, days: 0 }
  );

  const daysToMonthsAndDays = (totalDays) => {
    const months = Math.floor(totalDays / 30);
    const days = totalDays % 30;
    return { months, days };
  };

  const renderTimeValue = (value) => {
    if (!value || typeof value !== "object") return "N/A";

    const parts = [
      value.years > 0 ? `${value.years}y` : "",
      value.months > 0 ? `${value.months}m` : "",
      value.days > 0 ? `${value.days}d` : "",
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(" ") : "N/A";
  };

  const openAddBanModal = () => {
    setIsAddBanModalOpen(true);
  };

  const selectHouse = (houseName) => {
    setSelectedHouse(houseName);
  };

  const totalOccupiedBeds = Object.values(housesData || {}).reduce(
    (acc, house) => acc + (house.occupied || 0),
    0
  );

  const totalBeds = Object.values(housesData || {}).reduce(
    (acc, house) => acc + (house.totalBeds || 0),
    0
  );

  const percentageFull =
    totalBeds > 0 ? Math.round((totalOccupiedBeds / totalBeds) * 100) : 0;

  const memoizedSobrietyData = useMemo(() => {
    const data = {};
    Object.keys(housesData).forEach((house) => {
      const houseSobriety = calculateHouseSobriety(house);
      data[house] = houseSobriety || { years: 0, months: 0, days: 0 };
    });
    return data;
  }, [residents, housesData]);

  const updateUACount = async (residentId, count) => {
    if (!organizationId) return;

    try {
      const validCount = Math.max(0, parseInt(count) || 0);

      // First update the local state for immediate UI feedback
      setUAResidents((prevResidents) =>
        prevResidents.map((resident) =>
          resident.id === residentId
            ? { ...resident, uaCount: validCount }
            : resident
        )
      );

      const residentRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        residentId
      );

      await setDoc(
        residentRef,
        {
          uaCount: validCount,
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Error updating UA count:", error);
      // If there's an error, refresh the data to restore correct state
      fetchUAResidents();
    }
  };

  const toggleDocsModal = () => {
    setIsDocsModalOpen(!isDocsModalOpen);
    if (!isDocsModalOpen) fetchDocuments();
  };

  const toggleBannedModal = () => {
    setIsBannedModalOpen(!isBannedModalOpen);
  };

  const toggleUAListModal = () => {
    if (isUAListModalOpen) setSelectedHouse(null);
    setUAListModalOpen(!isUAListModalOpen);
  };

  const closeAddBanModal = () => {
    setIsAddBanModalOpen(false);
    setBanName("");
  };

  const handleNavigateToResidentDashboard = async () => {
    if (!user?.email || !organizationId) {
      console.error("Missing user email or organization ID");
      return;
    }

    try {
      // Get the user's details from the users collection
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        console.error("User document not found");
        return;
      }

      const userDetails = userDocSnap.data();

      // Find the resident record that matches this user
      const resident = residents.find(
        (r) =>
          r.firstName === userDetails.firstName &&
          r.lastName === userDetails.lastName &&
          r.currentResident === true
      );

      if (!resident) {
        console.error("No matching resident record found");
        alert(
          "Unable to find your resident profile. Please contact an administrator."
        );
        return;
      }

      // Set session storage to indicate this is a leader viewing their own dashboard
      sessionStorage.setItem("viewingResidentId", resident.id);
      sessionStorage.setItem(
        "viewingResidentName",
        `${resident.firstName} ${resident.lastName}`
      );

      // Navigate to the resident dashboard
      navigate("/residentdashboard");
    } catch (error) {
      console.error("Error navigating to resident dashboard:", error);
      alert("Error accessing your resident dashboard. Please try again.");
    }
  };

  const handleBanSubmit = async () => {
    try {
      if (!organizationId || !banName.trim()) {
        console.error("Missing required data for ban submission");
        return;
      }

      const [firstName, ...lastNameParts] = banName.trim().split(" ");
      const lastName = lastNameParts.join(" ");

      const residentsRef = collection(
        db,
        `organizations/${organizationId}/residents`
      );

      const newBannedResident = {
        firstName: firstName || "",
        lastName: lastName || "",
        fullName: banName.trim(),
        returnability: "No", // Changed to match the existing document
        createdAt: new Date().toISOString(),
      };

      await addDoc(residentsRef, newBannedResident);

      closeAddBanModal();
    } catch (error) {
      console.error("Error adding banned resident:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <SubscriptionProvider organizationId={organizationId}>
      <Helmet>
        <title>&bull; Dashboard</title>
      </Helmet>

      {!bannerDismissed && (
        <SubscriptionBanner onDismiss={() => setBannerDismissed(true)} />
      )}

      <div className="min-h-screen bg-[#0A0F1E] p-6 relative overflow-hidden">
        {/* Base gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0F1E] via-[#0A0F1E] to-[#0C1222]" />

        {/* Very subtle pattern overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>
        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-6">
              <div className="flex gap-3">
                <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
                <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
                <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
              </div>
              <div>
                {organizationName && (
                  <h1 className="text-4xl font-bold text-white tracking-tight">
                    {organizationName} Dashboard
                  </h1>
                )}
              </div>
            </div>

            {/* 👇 Leader dropdown moved under the header */}
            {role === "Leader" && (
              <div className="relative mt-4">
                <button
                  onClick={() =>
                    setShowDashboardDropdown(!showDashboardDropdown)
                  }
                  className="flex items-center gap-2 px-3 py-2 bg-slate-800/50 hover:bg-slate-800/70 
          text-white rounded-lg border border-slate-700/50 hover:border-slate-600 
          transition-all duration-200 text-sm"
                >
                  <User size={16} />
                  <span className="font-medium hidden sm:inline">
                    Leader Dashboard
                  </span>
                  <span className="font-medium sm:hidden">Leader</span>
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${
                      showDashboardDropdown ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showDashboardDropdown && (
                  <div
                    className="absolute left-0 top-full mt-2 w-56 sm:w-48 bg-slate-900/95 backdrop-blur-xl 
              rounded-lg border border-slate-700/50 shadow-xl z-50 max-w-[calc(100vw-2rem)]"
                  >
                    <div className="py-2">
                      <button
                        onClick={() => {
                          setShowDashboardDropdown(false);
                        }}
                        className="w-full px-4 py-3 text-left text-slate-300 hover:text-white 
                hover:bg-slate-800/50 transition-colors flex items-center gap-2 text-sm"
                      >
                        <div className="w-2 h-2 bg-cyan-500 rounded-full flex-shrink-0"></div>
                        <span className="truncate">Leader Dashboard</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowDashboardDropdown(false);
                          handleNavigateToResidentDashboard();
                        }}
                        className="w-full px-4 py-3 text-left text-slate-300 hover:text-white 
                hover:bg-slate-800/50 transition-colors flex items-center gap-2 text-sm"
                      >
                        <User size={14} className="flex-shrink-0" />
                        <span className="truncate">My Resident Dashboard</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Click outside to close dropdown */}
          {showDashboardDropdown && (
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowDashboardDropdown(false)}
            />
          )}

          {/* Main content area - organized in rows */}
          <div className="flex flex-col space-y-8">
            {/* Row 1: Sobriety Times and Bed Availability */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Sobriety Statistics Card */}
              <div className="group relative z-10 bg-slate-900/40 backdrop-blur-xl rounded-xl overflow-hidden transition-all duration-500 lg:col-span-2 h-[550px] border border-slate-700/20 shadow-lg shadow-slate-900/20 hover:shadow-2xl hover:border-slate-600/30 hover:bg-slate-900/60">
                {/* Ambient background glow */}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-500/5 to-transparent" />

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Header */}
                <div className="relative flex items-center justify-between p-4 border-b border-slate-700/30 bg-slate-800/20">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 rounded-lg shadow-sm">
                      <Clock className="text-red-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Total Sobriety Times
                    </h2>
                  </div>
                </div>

                {/* Content */}
                <div className="h-[calc(100%-65px)] p-6 overflow-y-auto hover:scrollbar-thumb-slate-400 scrollbar-thumb-slate-600 scrollbar-track-slate-800/50 scrollbar-thin relative z-20">
                  <div className="p-4 lg:p-6">
                    <DynamicGrid
                      items={[
                        ...Object.entries(housesData),
                        ...(Object.keys(housesData).length > 1
                          ? [["total", { color: "rgb(59, 130, 246)" }]]
                          : []),
                      ]}
                      renderItem={([houseName, houseData]) => (
                        <DynamicCard
                          houseName={houseName}
                          houseData={houseData}
                        >
                          <SobrietyCard
                            houseName={houseName}
                            houseData={houseData}
                            sobriety={
                              houseName === "total"
                                ? totalSobriety
                                : memoizedSobrietyData[houseName]
                            }
                            highs={highs}
                            hoveredCard={hoveredCard}
                            onHover={setHoveredCard}
                            onLeave={() => setHoveredCard(null)}
                          />
                        </DynamicCard>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Bed Platform */}
              <div className="group relative bg-slate-900/40 backdrop-blur-xl rounded-xl overflow-hidden transition-all duration-500 h-[550px] border border-slate-700/20 shadow-lg shadow-slate-900/20 hover:shadow-2xl hover:border-emerald-500/20 hover:bg-slate-900/60">
                {/* Ambient background glow */}
                <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 to-transparent" />

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="flex flex-col h-full">
                  {/* Header */}
                  <div className="relative flex items-center justify-between p-4 border-b border-slate-700/30 bg-slate-800/20">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-500/10 rounded-lg shadow-sm">
                        <Bed className="text-emerald-400" />
                      </div>
                      <h2 className="text-lg font-semibold text-white">
                        Available Beds
                      </h2>
                    </div>
                  </div>

                  {/* Main content */}
                  <div className="flex-1 p-6 overflow-y-auto relative z-20">
                    <div className="grid grid-cols-1 gap-6">
                      {Object.entries(housesData || {}).map(
                        ([houseName, houseData]) => {
                          const occupied = houseData?.occupied || 0;
                          const totalBeds = houseData?.totalBeds || 0;
                          const available =
                            totalBeds > 0 ? totalBeds - occupied : 0;
                          const percentage =
                            totalBeds > 0 ? (occupied / totalBeds) * 100 : 0;

                          return (
                            <SubscriptionGate>
                              <div
                                key={houseName}
                                className={`relative group/bed h-24 cursor-pointer transform transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]`}
                                onClick={() => {
                                  if (bedPlatformEnabled) {
                                    navigate(`/beds/${houseName}`);
                                  }
                                }}
                                role="button"
                                tabIndex={bedPlatformEnabled ? 0 : -1}
                                onKeyDown={(e) => {
                                  if (
                                    bedPlatformEnabled &&
                                    (e.key === "Enter" || e.key === " ")
                                  ) {
                                    navigate(`/beds/${houseName}`);
                                  }
                                }}
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-slate-800/30 to-transparent rounded-xl" />
                                <div
                                  style={{
                                    "--house-color": houseData.color,
                                    "--house-color-transparent": `${houseData.color}30`,
                                  }}
                                  className="relative h-full p-4 backdrop-blur-sm rounded-xl border border-slate-700/30
        hover:border-[--house-color] transition-all duration-500 flex flex-col justify-center
        hover:shadow-lg hover:shadow-[--house-color-transparent]"
                                >
                                  {/* All your inner content stays the same */}
                                  <div className="flex justify-between items-center mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-slate-200 font-medium">
                                        {houseName}
                                      </span>
                                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover/bed:translate-x-1 transition-transform" />
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {occupied === totalBeds && (
                                        <span
                                          style={{
                                            backgroundColor: `${houseData.color}20`,
                                            color: houseData.color,
                                          }}
                                          className="text-xs font-medium px-2 py-0.5 rounded-full animate-pulse"
                                        >
                                          Full
                                        </span>
                                      )}
                                      <span className="text-slate-300 text-sm bg-slate-800/40 px-2 py-1 rounded-md">
                                        {totalBeds > 0
                                          ? `${available} / ${totalBeds} beds`
                                          : "No data"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="h-2 bg-slate-700/30 rounded-full overflow-hidden shadow-inner">
                                    <div
                                      style={{
                                        width: `${percentage}%`,
                                        background: `linear-gradient(to right, ${houseData.color}CC, ${houseData.color})`,
                                      }}
                                      className="h-full transition-all duration-300"
                                    />
                                  </div>
                                </div>
                              </div>
                            </SubscriptionGate>
                          );
                        }
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="p-6 mt-auto border-t border-slate-700/30 bg-slate-800/20 relative z-20">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-slate-200 font-medium">
                        Total Occupancy
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-emerald-400">
                          {percentageFull}%
                        </span>
                      </div>
                    </div>

                    {checkPermission("viewWaitlist") && (
                      <SubscriptionGate>
                        <button
                          onClick={() => navigate("/waitlist")}
                          className="w-full py-3 px-4 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-xl
      transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-cyan-500/20 font-medium transform hover:scale-[1.02]
      active:scale-[0.98]"
                        >
                          Waitlist
                        </button>
                      </SubscriptionGate>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Relapse Tracking Card */}
              <div className="group relative z-10 bg-slate-900/40 backdrop-blur-xl rounded-xl overflow-hidden transition-all duration-500 lg:col-span-2 h-[400px] border border-slate-700/20 shadow-lg shadow-slate-900/20 hover:shadow-2xl hover:border-slate-600/30 hover:bg-slate-900/60">
                {/* Ambient background glow */}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-500/5 to-transparent" />

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Header */}
                <div className="relative flex items-center justify-between p-4 border-b border-slate-700/30 bg-slate-800/20">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500/10 rounded-lg">
                      <AlertTriangle className="text-amber-200" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Last Reoccurrence
                    </h2>
                  </div>
                </div>

                {/* Content */}
                <div className="h-[calc(100%-65px)] p-6 overflow-y-auto hover:scrollbar-thumb-slate-400 scrollbar-thumb-slate-600 scrollbar-track-slate-800/50 scrollbar-thin relative z-20">
                  <ReoccurrenceGrid
                    timeSinceLastRelapse={timeSinceLastRelapse}
                    formatRelapseTime={formatRelapseTime}
                  />
                </div>
              </div>

              {/* Impact Statistics Card */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500 h-[400px]">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative flex items-center justify-between p-4 border-b border-slate-800/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <Users className="text-blue-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Community Impact
                    </h2>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex flex-col items-center justify-center text-center space-y-4">
                    <div className="relative w-32 h-32">
                      <div className="absolute inset-0 rounded-full bg-amber-200/10 animate-ping" />
                      <div className="relative w-full h-full rounded-full border-4 border-amber-200/20 flex items-center justify-center">
                        <div>
                          <p className="text-4xl font-bold text-amber-200">
                            {totalHelped}+
                          </p>
                          <p className="text-sm text-slate-400">
                            Lives Impacted
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Updated Savings Rendering */}
                    <p className="text-xl font-bold text-amber-200">
                      {totalSavings > 0
                        ? `$${Number(totalSavings).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} Saved`
                        : "Calculating..."}
                    </p>

                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-slate-400">
                        Economic Savings to Society
                      </p>
                      <div
                        className="relative inline-block"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const tooltip = document.getElementById("tooltip");
                          if (tooltip) {
                            tooltip.style.left = `${rect.left}px`;
                            tooltip.style.top = `${rect.top - 10}px`;
                          }
                          setIsTooltipVisible(true);
                        }}
                        onMouseLeave={() => setIsTooltipVisible(false)}
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const tooltip = document.getElementById("tooltip");
                          if (tooltip) {
                            tooltip.style.left = `${rect.left}px`;
                            tooltip.style.top = `${rect.top - 10}px`;
                          }
                          setIsTooltipVisible(!isTooltipVisible);
                        }}
                      >
                        <Info
                          className="text-slate-400 hover:text-slate-300 cursor-pointer"
                          size={16}
                        />
                        {isTooltipVisible && (
                          <div
                            className="fixed transform -translate-x-full -translate-y-full w-72 p-3 bg-slate-800 text-white text-sm rounded-lg shadow-lg opacity-100 transition-opacity duration-200 z-[9999]"
                            style={{
                              whiteSpace: "normal",
                              overflowWrap: "break-word",
                              maxWidth: "100%",
                              minWidth: "200px",
                            }}
                          >
                            <p>
                              Updated Apr 2025&nbsp;—{" "}
                              <strong>
                                all figures are in 2025 dollars, CPI-adjusted
                              </strong>
                              . We combine the latest U.S. cost estimates for
                              alcohol misuse (<strong>$354 B</strong>),
                              non-opioid illicit drugs (<strong>$290 B</strong>
                              ), and opioid use disorder/overdose (
                              <strong>$1.22 T</strong>). Spread across roughly{" "}
                              <strong>50 million</strong> Americans with any SUD
                              (NSDUH 2022), this total forms the annual economic
                              burden used in our savings calculation.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Records Card */}
            {checkFeature("records") && (
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500 lg:col-span-2">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Header Section */}
                <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border-b border-slate-800/50 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-violet-500/10 rounded-lg">
                      <BarChart3 className="text-violet-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      Records
                    </h2>
                  </div>
                  <select
                    value={recordTypeFilter}
                    onChange={(e) => setRecordTypeFilter(e.target.value)}
                    className="w-full sm:w-auto bg-slate-800/50 text-white rounded-lg px-4 py-2 border border-slate-700/50"
                  >
                    <option value="sobriety">Sobriety Times</option>
                    <option value="relapse">Reoccurrence Times</option>
                    <option value="averageStay">Average Length of Stay</option>
                  </select>
                </div>

                {/* Content Section */}
                <div className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-track-slate-800/50 scrollbar-thumb-violet-500/20 hover:scrollbar-thumb-violet-500/30">
                    {!filteredRecords.length
                      ? Object.keys(housesData).map((houseName) => (
                          <div
                            key={houseName}
                            className="group/card relative bg-slate-800/30 rounded-xl border border-amber-200/10 hover:border-amber-200/20 hover:shadow-lg hover:shadow-amber-200/10 transition-all duration-300"
                          >
                            <div className="absolute inset-0 bg-gradient-to-b from-amber-600/5 to-transparent rounded-xl opacity-0 group-hover/card:opacity-100 transition-all duration-300" />
                            <div className="relative p-3 sm:p-4 flex flex-col items-center justify-center">
                              <h3 className="text-sm sm:text-base lg:text-lg text-center text-zinc-300 font-medium mb-2 sm:mb-3">
                                {`${houseName} - No Records`}
                              </h3>
                              <p className="text-base sm:text-lg lg:text-xl text-amber-200 font-semibold">
                                No records yet
                              </p>
                            </div>
                          </div>
                        ))
                      : filteredRecords.map((high, index) => {
                          if (!high) return null;

                          const label =
                            high.label === "averageLengthOfTime"
                              ? "Organization Average Stay"
                              : high.label?.replace(
                                  /([a-zA-Z]+-?\d*)(Sobriety|totalSobriety|Relapse|AverageLengthOfStay|averageLengthOfTime)/g,
                                  (match, houseName, type) => {
                                    const capitalizedHouseName =
                                      houseName.charAt(0).toUpperCase() +
                                      houseName
                                        .slice(1)
                                        .replace(/(-\d+)/, "$1 ");
                                    switch (type) {
                                      case "Sobriety":
                                        return `${capitalizedHouseName} Sobriety Record`;
                                      case "totalSobriety":
                                        return "Total Sobriety Record";
                                      case "Relapse":
                                        return `${capitalizedHouseName} Time Without Reoccurrence`;
                                      case "AverageLengthOfStay":
                                        return `${capitalizedHouseName} Average Stay`;
                                      case "averageLengthOfTime":
                                        return "Organization Average Stay";
                                      default:
                                        return capitalizedHouseName;
                                    }
                                  }
                                );

                          return (
                            <div
                              key={index}
                              className="group/card relative bg-slate-800/30 rounded-xl border border-amber-200/10 hover:border-amber-200/20 hover:shadow-lg hover:shadow-amber-200/10 transition-all duration-300"
                            >
                              <div className="absolute inset-0 bg-gradient-to-b from-amber-600/5 to-transparent rounded-xl opacity-0 group-hover/card:opacity-100 transition-all duration-300" />
                              <div className="relative p-3 sm:p-4 flex flex-col items-center justify-center">
                                <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 rounded-xl" />
                                <h3 className="text-sm sm:text-base lg:text-lg text-center text-zinc-300 font-medium mb-2 sm:mb-3">
                                  {label || "Unknown"}
                                </h3>
                                {!high.value ||
                                high.value === -Infinity ||
                                high.value === null ? (
                                  <p className="text-base sm:text-lg lg:text-xl text-amber-200 font-semibold">
                                    {label?.includes("Relapse")
                                      ? "No relapse yet"
                                      : "N/A"}
                                  </p>
                                ) : high.label === "averageLengthOfTime" ||
                                  high.label.includes("AverageLengthOfStay") ? (
                                  <p className="text-base sm:text-lg lg:text-xl text-amber-200 font-semibold">
                                    {(() => {
                                      const { months, days } =
                                        daysToMonthsAndDays(
                                          Math.round(high.value)
                                        );
                                      return `${months}m ${days}d`;
                                    })()}
                                  </p>
                                ) : (
                                  <p className="text-base sm:text-lg lg:text-xl text-amber-200 font-semibold">
                                    {renderTimeValue(high.value)}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                  </div>
                </div>
              </div>
            )}

            {/* Interactive Lists Section */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Banned List */}
              {checkPermission("manageBanList") ? (
                <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
                  <button onClick={toggleBannedModal} className="w-full">
                    <div
                      className={`${
                        isBannedModalOpen ? "rounded-t-xl" : "rounded-xl"
                      } p-4 flex items-center justify-between bg-slate-800/50`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-500/10 rounded-lg">
                          <ShieldOff className="text-red-400" />
                        </div>
                        <h2 className="text-xl font-semibold text-white">
                          Prohibited List
                        </h2>
                      </div>
                      {isBannedModalOpen ? (
                        <ChevronDown className="text-red-400 transition-transform w-5 h-5" />
                      ) : (
                        <ChevronRight className="text-red-400 transition-transform w-5 h-5" />
                      )}
                    </div>
                  </button>
                  {isBannedModalOpen && (
                    <div className="p-6 border-t border-slate-700">
                      <div className="space-y-3">
                        {bannedList.length === 0 ? (
                          <div className="text-slate-400 text-center p-3">
                            No banned users found
                          </div>
                        ) : (
                          bannedList.map((resident) => (
                            <div key={resident.id} className="text-left">
                              <div className="block p-3 bg-slate-800/30 hover:bg-slate-800/50 rounded-lg text-red-400 transition-all flex items-center justify-between">
                                <span className="truncate">
                                  {resident.firstName + " " + resident.lastName}
                                </span>
                                <ShieldOff className="flex-shrink-0 ml-2 w-5 h-5 text-red-400/70" />
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      {checkPermission("manageBanList") && (
                        <SubscriptionGate>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openAddBanModal();
                            }}
                            className="w-full py-2 px-4 mt-6 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg transition-all duration-300"
                          >
                            Add Name
                          </button>
                        </SubscriptionGate>
                      )}
                    </div>
                  )}
                </div>
              ) : null}

              {/* UA List */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
                <button onClick={toggleUAListModal} className="w-full">
                  <div
                    className={`${
                      isUAListModalOpen ? "rounded-t-xl" : "rounded-xl"
                    } p-4 flex items-center justify-between bg-slate-800/50`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-500/10 rounded-lg">
                        <ClipboardList className="text-purple-400" />
                      </div>
                      <h2 className="text-xl font-semibold text-white">
                        UA List
                      </h2>
                    </div>
                    {isUAListModalOpen ? (
                      <ChevronDown className="text-purple-400 transition-transform w-5 h-5" />
                    ) : (
                      <ChevronRight className="text-purple-400 transition-transform w-5 h-5" />
                    )}
                  </div>
                </button>
                {isUAListModalOpen && (
                  <div className="p-6 border-t border-slate-700">
                    {selectedHouse === null ? (
                      <div className="flex flex-col gap-4">
                        {Object.entries(housesData).map(
                          ([houseName, houseData]) => (
                            <button
                              key={houseName}
                              onClick={() => selectHouse(houseName)}
                              style={{
                                color: houseData.color,
                                borderColor: `${houseData.color}40`,
                                "--hover-border": `${houseData.color}60`,
                                boxShadow: `0 0 0 1px ${houseData.color}40`,
                              }}
                              className="w-full p-3 bg-slate-800/30 hover:bg-slate-800/50 rounded-lg border hover:border-[--hover-border] transition-all"
                            >
                              {houseName}
                            </button>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {uaResidents.map((resident) => (
                          <div
                            key={resident.id}
                            className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg"
                          >
                            <span className="text-slate-300">
                              {resident.firstName} {resident.lastName}
                            </span>
                            <input
                              className="w-16 px-2 py-1 bg-slate-700/50 border border-slate-600 rounded text-center text-white"
                              type="number"
                              min="0"
                              value={resident.uaCount || 0}
                              readOnly={false} // Force this to be always editable for testing
                              onChange={(event) => {
                                const newValue = event.target.value;
                                updateUACount(resident.id, newValue);
                              }}
                            />
                          </div>
                        ))}
                        <div className="flex justify-center items-center mt-4 sm:mt-6">
                          <button
                            onClick={() => setSelectedHouse(null)}
                            className="flex items-center gap-2 px-6 sm:px-8 py-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 rounded-lg text-slate-300 text-sm sm:text-base font-medium transition-all"
                          >
                            <ArrowLeft size={20} />
                            <span>Back</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Documents */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
                <button onClick={toggleDocsModal} className="w-full">
                  <div
                    className={`${
                      isDocsModalOpen ? "rounded-t-xl" : "rounded-xl"
                    } p-4 flex items-center justify-between bg-slate-800/50`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500/10 rounded-lg">
                        <FileText className="text-blue-400" />
                      </div>
                      <h2 className="text-xl font-semibold text-white">
                        Documents
                      </h2>
                    </div>
                    {isDocsModalOpen ? (
                      <ChevronDown className="text-blue-400 transition-transform w-5 h-5" />
                    ) : (
                      <ChevronRight className="text-blue-400 transition-transform w-5 h-5" />
                    )}
                  </div>
                </button>
                {isDocsModalOpen && (
                  <div className="p-6 border-t border-slate-700">
                    <div className="space-y-3">
                      {documents.length === 0 ? (
                        <div className="text-slate-400 text-center p-3">
                          No documents found
                        </div>
                      ) : (
                        documents.map((d) => (
                          <div key={d.id} className="text-left">
                            <a
                              className="block p-3 bg-slate-800/30 hover:bg-slate-800/50 rounded-lg text-blue-400 hover:text-blue-300 transition-all flex items-center justify-between"
                              target="_blank"
                              rel="noopener noreferrer"
                              href={d.fileURL}
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(
                                  d.fileURL,
                                  "_blank",
                                  "noopener,noreferrer"
                                );
                              }}
                            >
                              <span className="truncate">
                                {d.fileName || "Untitled Document"}
                              </span>
                              <FileText className="flex-shrink-0 ml-2 w-5 h-5 text-blue-400/70" />
                            </a>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showBedUpdateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-950/95 border border-slate-800 rounded-xl shadow-xl w-96 p-6">
            <h3 className="text-xl font-semibold text-white mb-4">
              Action Required
            </h3>
            <p className="text-slate-300 mb-6">
              Your current bed count ({organizationData?.currentTotalBeds})
              exceeds your paid limit ({organizationData?.paidBedCount}). Please
              update your house layout.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowBedUpdateModal(false);
                  setShowSettings(true);
                }}
                className="px-6 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Update Now
              </button>
            </div>
          </div>
        </div>
      )}
      {showSettings && (
        <SettingsModal
          onClose={() => setShowSettings(false)}
          initialTab="houses"
        />
      )}
      <Modal
        isOpen={isAddBanModalOpen}
        onClose={closeAddBanModal}
        title="Add to Ban List"
      >
        <div className="p-6 overflow-y-auto">
          <div className="space-y-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Resident Name"
                value={banName}
                onChange={(e) => setBanName(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 px-6 py-4 border-t border-slate-800">
          <button
            type="button"
            onClick={closeAddBanModal}
            className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleBanSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors"
          >
            Submit
          </button>
        </div>
      </Modal>
    </SubscriptionProvider>
  );
};

export default Dashboard;
